<template>
	<div v-if="canAccessSettings" class="new-settings-page">
		<div class="inbox-title sticky top-0 z-50 settings-title-container">
			<h1 class="inbox-title-label">
				{{ partnerName }}
			</h1>
		</div>
		<div class="new-settings">
			<!-- Secondary settings navigation -->
			<aside
				class="new-settings__sidebar"
				:aria-label="common.settingsNavigationAriaLabel"
			>
				<div class="new-settings__sidebar-header">
					<font-awesome-icon
						class="new-settings__sidebar-header-icon"
						fixed-width
						:icon="['fal', 'cog']"
						aria-hidden="true"
					/>
					<span class="new-settings__sidebar-header-label">{{
						common.settings
					}}</span>
				</div>

				<nav class="new-settings__nav" role="navigation">
					<button
						v-for="item in visibleNavItems"
						:key="item.id"
						type="button"
						class="new-settings__nav-item"
						:class="{
							'new-settings__nav-item--active':
								activeSectionId === item.id,
						}"
						:aria-current="
							activeSectionId === item.id ? 'page' : false
						"
						@click="selectSection(item.id)"
					>
						{{ item.label }}
					</button>
				</nav>
			</aside>

			<!-- Active settings section -->
			<main class="new-settings__content">
				<keep-alive :include="partnerFormKeepAliveNames">
					<component
						:is="activeSectionComponent"
						:key="activeSectionId"
						:organization-name="partnerName"
					/>
				</keep-alive>
			</main>
		</div>
	</div>
</template>

<script>
import _get from 'lodash/get'
import { mapActions, mapGetters } from 'vuex'
import OrganizationSection from '@/views/Settings/sections/OrganizationSection.vue'
import GeneralInformationSection from '@/views/Settings/sections/GeneralInformationSection.vue'
import OfficeHoursSection from '@/views/Settings/sections/OfficeHoursSection.vue'
import SecondaryVoicemailSection from '@/views/Settings/sections/SecondaryVoicemailSection.vue'
import EhrConfigurationSection from '@/views/Settings/sections/EhrConfigurationSection.vue'
import { invalidateEhrNoteTypesCache } from '@/views/Settings/sections/EhrConfigurationSection.vue'
import {
	SETTINGS_COMMON,
	SETTINGS_NAV_ITEMS,
	SETTINGS_SECTION_IDS,
} from '@/config/settingsConstants'

/**
 * Map section ids to their view components.
 *
 * @type {Object}
 */
const SECTION_COMPONENTS = {
	[SETTINGS_SECTION_IDS.ORGANIZATION]: OrganizationSection,
	[SETTINGS_SECTION_IDS.GENERAL_INFORMATION]: GeneralInformationSection,
	[SETTINGS_SECTION_IDS.OFFICE_HOURS]: OfficeHoursSection,
	[SETTINGS_SECTION_IDS.SECONDARY_VOICEMAIL]: SecondaryVoicemailSection,
	[SETTINGS_SECTION_IDS.EHR_CONFIGURATION]: EhrConfigurationSection,
}

/** Sections that share one partner fetch and stay mounted while navigating. */
const PARTNER_FORM_KEEP_ALIVE_NAMES = [
	'GeneralInformationSection',
	'SecondaryVoicemailSection',
	'EhrConfigurationSection',
]

export default {
	/**
	 * Allow child sections to switch sidebar navigation.
	 *
	 * @return {Object}
	 */
	provide() {
		return {
			selectSettingsSection: this.selectSection,
			ensureSettingsPartnerLoaded: this.ensureSettingsPartnerLoaded,
			invalidateSettingsPartnerCache: this.invalidateSettingsPartnerCache,
		}
	},

	/**
	 * The component's registered child components.
	 *
	 * @type {Object}
	 */
	components: {
		OrganizationSection,
		GeneralInformationSection,
		OfficeHoursSection,
		SecondaryVoicemailSection,
		EhrConfigurationSection,
	},

	/**
	 * The component's computed properties.
	 *
	 * @type {Object}
	 */
	computed: {
		canAccessSettings() {
			return (
				(this.hasOfficeManagerAccess && this.clarusAppType) ||
				this.isOrgAdmin ||
				this.isOrganizationOnlyAdmin
			)
		},

		/**
		 * Sidebar items filtered by partner capabilities and user role.
		 * Organization is shown for org admins and organization-only admins.
		 *
		 * @return {Array}
		 */
		visibleNavItems() {
			if (this.isOrganizationOnlyAdmin) {
				return this.navItems.filter(
					item => item.id === SETTINGS_SECTION_IDS.ORGANIZATION
				)
			}

			return this.navItems.filter(item => {
				if (item.id === SETTINGS_SECTION_IDS.ORGANIZATION) {
					return this.isOrgAdmin || this.isOrganizationOnlyAdmin
				}

				if (item.id !== SETTINGS_SECTION_IDS.EHR_CONFIGURATION) {
					return true
				}

				return (
					this.isEhrConfigured &&
					(this.isAdmin || this.isPartnerAdmin)
				)
			})
		},

		/**
		 * Vue component rendered in the main content area.
		 *
		 * @return {Object}
		 */
		activeSectionComponent() {
			return SECTION_COMPONENTS[this.activeSectionId]
		},

		/**
		 * Component names cached by keep-alive for partner form sections.
		 *
		 * @return {Array}
		 */
		partnerFormKeepAliveNames() {
			return PARTNER_FORM_KEEP_ALIVE_NAMES
		},

		/**
		 * Header title: organization name when Organization section is visible.
		 *
		 * @return {String}
		 */
		partnerName() {
			const isOrganizationSectionVisible =
				this.activeSectionId === SETTINGS_SECTION_IDS.ORGANIZATION &&
				(this.isOrgAdmin || this.isOrganizationOnlyAdmin)

			if (isOrganizationSectionVisible) {
				return (
					this.organizationProfile?.business_name ||
					this.organizationProfile?.name ||
					this.partner?.name ||
					''
				)
			}

			return this.partner?.name || ''
		},

		/**
		 * Whether the active partner has EHR settings configured.
		 *
		 * @return {Boolean}
		 */
		isEhrConfigured() {
			return Boolean(_get(this.$store.getters, 'partner.ehr_settings'))
		},

		...mapGetters({
			hasOfficeManagerAccess: 'auth/hasOfficeManagerAccess',
			isAdmin: 'auth/isAdmin',
			isPartnerAdmin: 'auth/isPartnerAdmin',
			isOrgAdmin: 'auth/isOrgAdmin',
			isOrganizationOnlyAdmin: 'auth/isOrganizationOnlyAdmin',
			partner: 'partners/active',
			clarusAppType: 'partners/clarusAppType',
			isEngageApp: 'partners/isEngageApp',
			organizationProfile: 'settings/organizationProfile',
		}),
	},

	/**
	 * The component's local methods.
	 *
	 * @type {Object}
	 */
	methods: {
		ensureSettingsAccess() {
			if (this.canAccessSettings) {
				return
			}

			const fallbackRoute = this.clarusAppType
				? { name: 'app.calls' }
				: this.isEngageApp
				? { name: 'app.engage' }
				: { name: 'app.dashboard' }

			this.$router.replace(fallbackRoute).catch(() => {})
		},

		/**
		 * Switch the visible settings section when a sidebar item is selected.
		 *
		 * @param {String} sectionId
		 * @return {void}
		 */
		selectSection(sectionId) {
			if (!this.isSectionNavigable(sectionId)) {
				return
			}

			if (this.activeSectionId === sectionId) {
				this.syncSectionRoute(sectionId)
				return
			}

			this.activeSectionId = sectionId
			this.syncSectionRoute(sectionId)
		},

		/**
		 * Whether a section id is currently visible in settings navigation.
		 *
		 * @param {String} sectionId
		 * @return {Boolean}
		 */
		isSectionNavigable(sectionId) {
			return this.visibleNavItems.some(item => item.id === sectionId)
		},

		/**
		 * Open the section from the URL path (e.g. /settings/officeHours).
		 *
		 * @return {void}
		 */
		applySectionFromRoute() {
			const sectionId = this.$route.params.section

			if (!sectionId || !this.isSectionNavigable(sectionId)) {
				return
			}

			this.activeSectionId = sectionId
		},

		/**
		 * Keep the URL in sync with the active settings section.
		 *
		 * @param {String} sectionId
		 * @return {void}
		 */
		syncSectionRoute(sectionId) {
			if (this.$route.name !== 'app.settings') {
				return
			}

			if (this.$route.params.section === sectionId) {
				return
			}

			this.$router
				.replace({
					name: 'app.settings',
					params: { ...this.$route.params, section: sectionId },
					query: this.$route.query,
				})
				.catch(() => {})
		},

		/**
		 * Clear cached partner load state (e.g. when route partner changes).
		 *
		 * @return {void}
		 */
		invalidateSettingsPartnerCache() {
			this.settingsPartnerLoadedForId = null
			this.settingsPartnerLoadPromise = null
			invalidateEhrNoteTypesCache()
		},

		/**
		 * Load partner once per partner id; dedupe concurrent requests.
		 *
		 * @param {Boolean} [force=false]
		 * @return {Promise<void>}
		 */
		ensureSettingsPartnerLoaded(force = false) {
			const partnerId = parseInt(this.$route.params.partner, 10)

			if (!partnerId) {
				return Promise.resolve()
			}

			if (!force && this.settingsPartnerLoadedForId === partnerId) {
				return Promise.resolve()
			}

			if (!force && this.settingsPartnerLoadPromise) {
				return this.settingsPartnerLoadPromise
			}

			this.settingsPartnerLoadPromise = this.getPartner(partnerId)
				.then(() => {
					this.settingsPartnerLoadedForId = partnerId
				})
				.finally(() => {
					this.settingsPartnerLoadPromise = null
				})

			return this.settingsPartnerLoadPromise
		},

		...mapActions({
			getPartner: 'partners/find',
		}),
	},

	name: 'NewSetting',

	/**
	 * The component's property watchers.
	 *
	 * @type {Object}
	 */
	watch: {
		canAccessSettings(canAccess) {
			if (!canAccess) {
				this.ensureSettingsAccess()
			}
		},

		'$route.params.partner'(newId, oldId) {
			if (newId !== oldId) {
				this.invalidateSettingsPartnerCache()
				this.ensureSettingsPartnerLoaded()
			}
		},

		'$route.params.section'() {
			this.applySectionFromRoute()
		},

		/**
		 * Ensure the active section remains valid when nav items change.
		 *
		 * @return {void}
		 */
		visibleNavItems: {
			immediate: true,
			handler(items) {
				const isActiveVisible = items.some(
					item => item.id === this.activeSectionId
				)

				if (!isActiveVisible && items.length > 0) {
					this.activeSectionId = items[0].id
				}

				this.applySectionFromRoute()

				if (!this.$route.params.section && items.length > 0) {
					this.syncSectionRoute(this.activeSectionId)
				}
			},
		},
	},

	async mounted() {
		this.ensureSettingsAccess()

		if (!this.canAccessSettings) {
			return
		}

		await this.ensureSettingsPartnerLoaded()
		this.applySectionFromRoute()

		if (!this.$route.params.section) {
			this.syncSectionRoute(this.activeSectionId)
		}
	},

	/**
	 * Get the component's initial state.
	 *
	 * @return {Object}
	 */
	data() {
		return {
			activeSectionId: SETTINGS_SECTION_IDS.ORGANIZATION,
			common: SETTINGS_COMMON,
			settingsPartnerLoadedForId: null,
			settingsPartnerLoadPromise: null,
			navItems: SETTINGS_NAV_ITEMS,
		}
	},
}
</script>
